import React from 'react';
import PurchaseGiftCardOptions from './PurchaseGiftCardOptions';
import SuperPaymentForm from '../components/SuperPaymentForm';
import { Formik, Form, useFormikContext } from 'formik';
import * as Yup from 'yup';
import { Redirect, Route, Switch } from 'react-router-dom';
import { OrderingConstants, OrderingSelectors } from 'polygon-ordering';
import { useAppSelector } from '../app/hooks';
import NonMemberGiftCardFields from './NonMemberGiftCardFields';
import GiftCardPurchaseRedcatPay from './GiftCardPurchaseRedcatPay';
import { Show } from './atoms/Show';
import { useTranslation } from 'react-i18next';
import { noXssCharsTest } from '../utils/yupSchemaTests';
import StandardButton from './StandardButton';
import LoyaltyPaymentScreen from '../screens/LoyaltyPaymentScreen';
import history from '../history';
import {
  PURCHASE_GIFT_CARD_ROUTE,
  SELF_FIELDS_ROUTE,
  SELF_PAYMENT_ROUTE,
} from '../constants/loyaltyRoutes';

const { getMember, getGatewayType } = OrderingSelectors;
const { PAYMENT_GATEWAY_TYPES } = OrderingConstants;

const PurchaseGiftCardForSelfFields = () => {
  const { t } = useTranslation();
  const member = useAppSelector(getMember);
  const gatewayType = useAppSelector(getGatewayType);
  const { isValid } = useFormikContext<any>();

  return (
    <div>
      {!member && <NonMemberGiftCardFields />}
      <PurchaseGiftCardOptions />
      <Show when={gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY}>
        <div style={styles.buttonContainer}>
          <StandardButton
            label={t('button.proceedToPayment')}
            disabled={!isValid}
            onClick={() => history.push(SELF_PAYMENT_ROUTE)}
            themeKey="loyaltyButton"
            containerStyle={styles.button}
          />
        </div>
      </Show>
      <Show when={gatewayType !== PAYMENT_GATEWAY_TYPES.REDCAT_PAY}>
        <SuperPaymentForm
          route={member ? 'memberPurchaseGiftCardForSelf' : 'nonMemberPurchaseGiftCardForSelf'}
        />
      </Show>
    </div>
  );
};

const PurchaseGiftCardForSelf = () => {
  const { t } = useTranslation();
  const member = useAppSelector(getMember);
  const gatewayType = useAppSelector(getGatewayType);
  const maxGiftCardValue = useAppSelector(state => state.config.maxGiftCardValue);

  const baseSchema = {
    Amount: Yup.number()
      .required(t('form.required'))
      .min(1)
      .test('is-less-than-max-amount', `Maximum $${maxGiftCardValue}`, value => {
        if (value) return Number(value) <= maxGiftCardValue;
        return false;
      }),
  };

  const nonMemberSchema = {
    ...baseSchema,
    FirstName: noXssCharsTest.required(t('form.required')),
    LastName: noXssCharsTest.required(t('form.required')),
    Email: Yup.string().email(t('form.emailValidation')).required(t('form.required')),
    ConfirmEmail: Yup.string()
      .email(t('form.emailValidation'))
      .required(t('form.required'))
      .oneOf([Yup.ref('Email')], t('form.valuesMismatch')),
  };

  const memberInitialValues = { Amount: '' };
  const nonMemberInitialValues = {
    Amount: '',
    FirstName: '',
    LastName: '',
    Email: '',
    ConfirmEmail: '',
  };

  return (
    <Formik
      enableReinitialize
      validationSchema={
        member ? Yup.object().shape(baseSchema) : Yup.object().shape(nonMemberSchema)
      }
      initialValues={member ? memberInitialValues : nonMemberInitialValues}
      onSubmit={() => {}}
      validateOnMount={true}
    >
      {({ handleSubmit }) => (
        <Form onSubmit={handleSubmit}>
          {gatewayType === PAYMENT_GATEWAY_TYPES.REDCAT_PAY ? (
            <Switch>
              <Redirect exact from={`${PURCHASE_GIFT_CARD_ROUTE}/self`} to={SELF_FIELDS_ROUTE} />
              <Route path={SELF_FIELDS_ROUTE}>
                <PurchaseGiftCardForSelfFields />
              </Route>
              <Route path={SELF_PAYMENT_ROUTE}>
                <LoyaltyPaymentScreen>
                  <GiftCardPurchaseRedcatPay />
                </LoyaltyPaymentScreen>
              </Route>
            </Switch>
          ) : (
            <PurchaseGiftCardForSelfFields />
          )}
        </Form>
      )}
    </Formik>
  );
};

const styles: Styles = {
  buttonContainer: {
    marginTop: 20,
    display: 'flex',
    justifyContent: 'center',
  },
  button: {
    borderRadius: 5,
    height: 40,
    width: '100%',
  },
};

export default PurchaseGiftCardForSelf;
